/**
 * Layout route for the /guide area: a slim topic rail (sticky left column on
 * desktop, a horizontally scrolling pill strip on mobile) beside the matched
 * child topic in an <Outlet />. The Admin group is hidden for non-admins — the
 * same filter the shell's own nav uses; the /guide/admin page itself also
 * route-guards, so a hidden link is defence-in-depth, not the only gate.
 *
 * Active state: exact match for the Overview entry (/guide), prefix match for
 * every topic so deep links stay highlighted.
 */

import { Boxes, Gauge, type LucideIcon, MapPin, Shield, Snowflake, Workflow } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router";

import { useMe } from "@/lib/api";
import { cn } from "@/lib/utils";

type GuideTopic = {
  href: string;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
};

type GuideGroup = {
  label: string;
  adminOnly?: boolean;
  topics: GuideTopic[];
};

const GROUPS: GuideGroup[] = [
  {
    label: "Guide",
    topics: [
      { href: "/guide", label: "Overview", icon: Boxes },
      { href: "/guide/how-it-works", label: "How it works", icon: Workflow },
    ],
  },
  {
    label: "Surfaces",
    topics: [
      // The analysis screens carried in the rail, same order. Ask the agent
      // and Dashboard were retired here alongside their rail entries — both are
      // being adapted and will return (see overview.tsx's roadmap panel).
      { href: "/guide/scenarios", label: "Scenarios", icon: Gauge },
      { href: "/guide/location", label: "Location review", icon: MapPin },
      { href: "/guide/nonmovers", label: "Non-movers", icon: Snowflake },
    ],
  },
  {
    label: "Admin",
    adminOnly: true,
    topics: [{ href: "/guide/admin", label: "Admin", icon: Shield, adminOnly: true }],
  },
];

export function GuideShell() {
  const pathname = useLocation().pathname;
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";

  const visibleGroups = GROUPS.filter((group) => !group.adminOnly || isAdmin).map((group) => ({
    ...group,
    topics: group.topics.filter((topic) => !topic.adminOnly || isAdmin),
  }));

  return (
    <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 px-6 py-8 lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-10 lg:px-10 lg:py-10">
      <aside className="lg:sticky lg:top-8 lg:self-start">
        <nav className="flex flex-col gap-5">
          {visibleGroups.map((group) => (
            <div key={group.label}>
              <p className="text-muted px-2 pb-2 text-[10px] font-medium tracking-[0.18em] uppercase">
                {group.label}
              </p>
              <ul className="flex flex-row gap-1 overflow-x-auto pb-1 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:pb-0">
                {group.topics.map((topic) => (
                  <TopicRow key={topic.href} topic={topic} pathname={pathname} />
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

function TopicRow({ topic, pathname }: { topic: GuideTopic; pathname: string }) {
  // Overview is exact-match (every topic path is a prefix of /guide); topics
  // use prefix-match so a nested deep link keeps the rail highlighted.
  const isActive =
    topic.href === "/guide"
      ? pathname === "/guide"
      : pathname === topic.href || pathname.startsWith(topic.href + "/");
  const Icon = topic.icon;

  return (
    <li className="shrink-0 lg:shrink">
      <Link
        to={topic.href}
        className={cn(
          "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors",
          isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-warm-gray-02",
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        <span className="truncate">{topic.label}</span>
      </Link>
    </li>
  );
}
